import { Box, Button, Card, CardContent, Container, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import SiteFooterLinks from '../components/SiteFooterLinks';

const heroBackgroundTextureUrl = '/home/hero-background.webp';
const heroImageUrl = '/home/hero-portrait-1200.webp';
const heroImageSrcSet = '/home/hero-portrait-1200.webp 1200w, /home/hero-portrait-1800.webp 1800w';
const heroGridOverlayUrl = '/home/hero-grid-overlay.svg';
const connectImageUrl = '/home/connect-image-900.webp';
const connectImageSrcSet = '/home/connect-image-900.webp 900w, /home/connect-image-1400.webp 1400w';
const howItWorksCard1 = '/home/how-it-works-1.webp';
const howItWorksCard2 = '/home/how-it-works-2.webp';
const howItWorksCard3 = '/home/how-it-works-3.webp';

const howItWorksItems = [
  {
    title: 'Take a Diagnostic',
    description:
      'Students complete a diagnostic assessment that reveals strengths and gaps in foundational math skills.',
    image: howItWorksCard1,
  },
  {
    title: 'See the Full Picture',
    description:
      'alMATHEMA pinpoints the exact skills that need attention, giving students a clear view of what is solid and what needs review.',
    image: howItWorksCard2,
  },
  {
    title: 'Act with Confidence',
    description:
      'Students leave with a prioritized skill list and resources to act on immediately, and educators see exactly how to support them.',
    image: howItWorksCard3,
  },
];

function HomePage() {
  const theme = useTheme();
  const { almathema } = theme.palette;
  const [heroOrbitRotation, setHeroOrbitRotation] = useState(0);

  useEffect(() => {
    let rafId: number | null = null;

    const updateRotation = () => {
      setHeroOrbitRotation(window.scrollY * 0.12);
      rafId = null;
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(updateRotation);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <Box sx={{ backgroundColor: almathema.white }}>
      {/* Hero Banner */}
      <Box
        sx={{
          position: 'relative',
          color: 'primary.contrastText',
          overflow: 'hidden',
          backgroundImage: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%), url(${heroBackgroundTextureUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-between" sx={{ pt: { xs: 6, md: 5 } }}>
            <Box sx={{ maxWidth: 500, pb: { xs: 6, md: 0 } }}>
              <Typography variant="h2" sx={{ fontStyle: 'italic', fontWeight: 600, lineHeight: 1.15 }}>
                Beyond the
              </Typography>
              <Typography variant="h2" sx={{ color: almathema.sunYellow, fontStyle: 'italic', fontWeight: 700, lineHeight: 1.15, mb: 2 }}>
                Algebra Barrier
              </Typography>
              <Typography
                sx={{
                  mb: 4,
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 400,
                  lineHeight: '1rem',
                  letterSpacing: 0,
                  color: almathema.babyBlueIce,
                }}
              >
                alMATHEMA is a free platform that identifies the specific math skills students need to strengthen before gaps impact their academic success. Students know what to focus on and how to get started, and educators see exactly where their students stand.
              </Typography>

              <Stack direction="row" spacing={2}>
                <Button
                  component={RouterLink}
                  to="/register"
                  state={{ preselectedRole: 'student' }}
                  variant="contained"
                  sx={{
                    px: '2.375rem',
                    backgroundColor: almathema.deepSapphire,
                    border: 1,
                    borderColor: almathema.sapphire,
                    color: almathema.white,
                    fontSize: '1rem',
                  }}
                >
                  For Students
                </Button>
                <Button
                  component={RouterLink}
                  to="/register"
                  state={{ preselectedRole: 'educator' }}
                  variant="outlined"
                  sx={{
                    px: '2.375rem',
                    color: almathema.white,
                    borderColor: almathema.white,
                    fontSize: '1rem',
                  }}
                >
                  For Educators
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                width: { xs: '18rem', md: '38rem' },
                height: { xs: '22rem', md: '46rem' },
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 0,
                ml: { md: '-2rem' },
              }}
            >
              <Box
                component="img"
                src={heroImageUrl}
                srcSet={heroImageSrcSet}
                sizes="(max-width: 900px) 18rem, 38rem"
                alt="Student portrait"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: '52% 14%',
                  transform: 'scaleX(-1)',
                }}
              />
              <Box
                component="img"
                src={heroGridOverlayUrl}
                alt=""
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: { xs: '1.5%', md: '5%' },
                  right: { xs: '-22%', md: '-7%' },
                  width: { xs: '84%', md: '85%' },
                  maxWidth: { xs: '19rem', md: '33.5rem' },
                  height: 'auto',
                  aspectRatio: '819 / 759',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  opacity: 0.5,
                  pointerEvents: 'none',
                  mixBlendMode: 'screen',
                }}
              />
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: { xs: '-8.4rem', md: '-9.6rem' },
                  right: { xs: '-8rem', md: '-9.7rem' },
                  width: { xs: '14.25rem', md: '18.75rem' },
                  height: { xs: '14.25rem', md: '18.75rem' },
                  pointerEvents: 'none',
                  transform: `rotate(${heroOrbitRotation}deg)`,
                  transformOrigin: 'center center',
                  willChange: 'transform',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    border: `0.125rem solid ${almathema.babyBlueIce}`,
                    borderRadius: '50%',
                    opacity: 0.95,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: { xs: '0.625rem', md: '0.75rem' },
                    height: { xs: '0.625rem', md: '0.75rem' },
                    borderRadius: '50%',
                    backgroundColor: almathema.sunYellow,
                    transform: {
                      xs: 'translate(-50%, -50%) rotate(110deg) translateX(7.125rem)',
                      md: 'translate(-50%, -50%) rotate(110deg) translateX(9.375rem)',
                    },
                    zIndex: 1,
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ----------------- About Us Section ----------------- */}
      <Box id="about-us-section" sx={{ mt: { xs: -6, md: -13 }, pb: 7, position: 'relative', zIndex: 5 }}>
        <Container maxWidth="lg">
          <Card sx={{ backgroundColor: 'primary.main', color: 'common.white', borderRadius: 0, py: { xs: 3, md: 5 } }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={20} alignItems="stretch" sx={{ mb: 15 }}>
                <Box sx={{ flex: 1.2 }}>
                  <Typography variant="h3" sx={{ color: almathema.sunYellow, fontStyle: 'italic', mb: 2 }}>
                    Who We Serve
                  </Typography>
                  <Typography sx={{ color: 'secondary.light', mb: 2 }}>
                    alMATHEMA supports students, educators, and institutions working across algebra-dependent math pathways.
                  </Typography>
                  <Typography sx={{ color: almathema.sunYellow, opacity: 0.95 }}>
                    Students preparing for courses such as College Algebra, Precalculus, or Calculus use alMATHEMA to understand where they stand and what to focus on.
                  </Typography>
                  <Typography sx={{ color: almathema.sunYellow, opacity: 0.95 }}>
                    Educators and advisors use alMATHEMA to see student readiness clearly and provide targeted support.
                  </Typography>
                </Box>

                <Card sx={{ flex: 1, backgroundColor: 'background.paper', borderRadius: 0, p: { xs: 1, md: 2 } }}>
                  <CardContent
                    sx={{
                      p: { xs: 3, md: 4 },
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="h3" sx={{ color: 'primary.main', fontStyle: 'italic', mb: 2 }}>
                      About Us
                    </Typography>
                    <Typography color="primary.main" sx={{ mb: 3 }}>
                      Built by math educators, alMATHEMA makes student skill gaps visible and actionable.
                    </Typography>
                    <Button
                      variant="contained"
                      component={RouterLink}
                      to="/about-us"
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Stack>

              <Box sx={{ mt: 5 }}>
                <Typography variant="h3" sx={{ color: almathema.sunYellow, fontStyle: 'italic', mb: 3 }}>
                  How It Works
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
                  {howItWorksItems.map((item) => (
                    <Card key={item.title} sx={{ flex: 1, backgroundColor: 'primary.main', color: 'common.white', boxShadow: 'none' }}>
                      <CardContent>
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.title}
                          loading="lazy"
                          decoding="async"
                          sx={{ width: '100%', height: 350, objectFit: 'cover', borderRadius: 0, mb: 2 }}
                        />
                        <Typography sx={{ fontWeight: 700, mb: 1, color: 'almathema.sunYellow' }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'secondary.light' }}>{item.description}</Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Engagement Section */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ color: 'primary.dark', fontStyle: 'italic', textAlign: 'center', mb: 4 }}>
            What Students and Educators Are Saying
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} justifyContent="center">
            <Card
              sx={{
                flex: 1,
                maxWidth: 372,
                backgroundColor: almathema.babyBlueIce,
                borderRadius: '2rem',
                py: { xs: 3.5, md: 4.5 },
                px: { xs: 1.5, md: 2 },
              }}
            >
              <CardContent sx={{ p: { xs: 3.5, md: 4.5 } }}>
                <Stack spacing={{ xs: 3.5, md: 4.75 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box>
                      <Typography sx={{ color: almathema.sapphire, fontSize: '1rem', lineHeight: 1.2 }}>
                        Breaking skills down to their algebraic foundations is highly valuable.
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
            <Card
              sx={{
                flex: 1,
                maxWidth: 372,
                backgroundColor: 'primary.main',
                color: 'common.white',
                borderRadius: '2rem',
                py: { xs: 3.5, md: 4.5 },
                px: { xs: 1.5, md: 2 },
              }}
            >
              <CardContent sx={{ p: { xs: 3.5, md: 4.5 } }}>
                <Stack spacing={{ xs: 3.5, md: 4.75 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box>
                      <Typography sx={{ color: almathema.babyBlueIce, fontSize: '1rem', lineHeight: 1.2 }}>
                        The feedback clearly highlights strengths and areas for review.
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
            <Card
              sx={{
                flex: 1,
                maxWidth: 372,
                backgroundColor: almathema.sunYellow,
                borderRadius: '2rem',
                py: { xs: 3.5, md: 4.5 },
                px: { xs: 1.5, md: 2 },
              }}
            >
              <CardContent sx={{ p: { xs: 3.5, md: 4.5 } }}>
                <Stack spacing={{ xs: 3.5, md: 4.75 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box>
                      <Typography sx={{ color: almathema.sapphire, fontSize: '1rem', lineHeight: 1.2 }}>
                        Weaknesses can be identified early, before the first exam.
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Container>
      </Box>

      {/* Contact Us Section */}
      <Box id="contact-us-section" sx={{ backgroundColor: 'primary.main', pt: { xs: 6, md: 8 }, color: 'common.white' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
            <Box
              component="img"
              src="/logo-almathema.svg"
              alt="alMATHEMA"
              sx={{ width: { xs: 220, md: 300 }, height: 'auto' }}
            />
            <Typography variant="h5" sx={{ fontStyle: 'italic', mt: 1, textAlign: 'center' }}>Beyond the Algebra Barrier</Typography>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center">
            <Box sx={{ flex: 1, maxWidth: 320 }}>
              <Typography variant="h3" sx={{ fontStyle: 'italic', mb: 2 }}>Let’s Connect</Typography>
              <Typography sx={{ color: almathema.babyBlueIce, mb: 3, fontSize: '0.95rem', lineHeight: 1.35 }}>
                alMATHEMA is growing through collaboration with students, educators, and institutions.
              </Typography>
              <Typography sx={{ color: almathema.babyBlueIce, mb: 3, fontSize: '0.95rem', lineHeight: 1.35 }}>
                Students can reach out with questions or to share their experience.
              </Typography>
              <Typography sx={{ color: almathema.babyBlueIce, mb: 3, fontSize: '0.95rem', lineHeight: 1.35 }}>
                Educators and institutions can connect to pilot alMATHEMA, explore collaboration, or help shape the platform.
              </Typography>
              <Button
                variant="contained"
                component="a"
                href="mailto:contact@almathema.com"
                sx={{
                  backgroundColor: almathema.sunYellow,
                  color: almathema.sapphire,
                  fontSize: '1rem',
                  '&:hover': { backgroundColor: almathema.sunYellowHover },
                }}
              >
                Contact Us
              </Button>
            </Box>
            <Box sx={{ flex: 1, maxWidth: 580, display: 'flex', alignItems: 'flex-end', alignSelf: { md: 'flex-end' }, pl: { md: 5 } }}>
              <Box
                component="img"
                src={connectImageUrl}
                srcSet={connectImageSrcSet}
                sizes="(max-width: 900px) 100vw, 580px"
                alt="Students in connect section"
                loading="lazy"
                decoding="async"
                sx={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: 0 }}
              />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Footer Section */}
      <Box sx={{ backgroundColor: 'common.white', color: 'primary.main' }}>
        <Container maxWidth="lg" sx={{ py: 0 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch">
            <SiteFooterLinks variant="home" />

            <Card
              sx={{
                flex: 1,
                backgroundColor: almathema.sunYellow,
                borderRadius: 0,
                py: 3,
                pl: 3,
                position: 'relative',
                overflow: 'visible',
                '&::after': {
                  content: '""',
                  display: { xs: 'none', md: 'block' },
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '100%',
                  width: '100vw',
                  backgroundColor: almathema.sunYellow,
                },
              }}
            >
              <CardContent>
                <Typography variant="h2" sx={{ color: 'primary.main', fontStyle: 'italic', mb: 2 }}>
                  Ready to move beyond the algebra barrier?
                </Typography>
                <Button variant="contained" component={RouterLink} to="/register">
                  Get Started
                </Button>
              </CardContent>
            </Card>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default HomePage;
